<script setup lang="ts">
import debounce from 'lodash/debounce.js'
import { useMembershipStore } from '~/stores/membership'
import { useOpenPDFViewer } from '~~/composables/open-pdf-viewer'
import IconDownload from '@/assets/icons/download.svg'

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
  linkFlipHtml5: {
    type: String,
    default: null,
  },
  isDownloading: {
    type: Boolean,
    required: true,
  },
  downloadProgress: {
    type: Number,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  subscriptionRequired: {
    type: String,
    default: undefined,
  },
})

const { $auth } = useNuxtApp()
const membershipStore = useMembershipStore()
const { openPDFViewer } = useOpenPDFViewer()

const isAuthorized = computed(() => {
  return $auth.store.isAuthenticated && membershipStore.isSubscriberActive
})

const unAuthLabel = computed(() =>
  !isAuthorized.value ? 'Find out more' : props.label,
)

const emit = defineEmits(['onShareClick', 'onDownloadClick'])

const onDownloadClick = () => {
  emit('onDownloadClick')
  debouncedOverlayOptions()
}

const showOverlayOptions = ref(false)
const debouncedOverlayOptions = debounce(() => {
  showOverlayOptions.value = false
}, 3000)
const openOverlayOptions = () => {
  showOverlayOptions.value = true
  debouncedOverlayOptions()
}

const showViewNow = computed(() => {
  return isAuthorized.value && props.linkFlipHtml5
})
const handleOpenPDFViewer = () => {
  if (!props.linkFlipHtml5 || !props.documentId) return
  openPDFViewer(
    props.documentId,
    props.title,
    props.linkFlipHtml5,
    'presentation',
    props.subscriptionRequired,
  )
}
</script>

<template>
  <div class="absolute left-0 top-0 z-10 h-full w-full text-white">
    <div
      class="absolute left-0 top-0 hidden h-full w-full items-center justify-center bg-black/80 text-white transition-opacity duration-500 md:flex md:group-hover:opacity-100"
      :class="{
        'opacity-0': !isDownloading,
        '!flex opacity-100': showOverlayOptions || isDownloading,
      }"
    >
      <BaseButton
        class="h-[42px] min-w-[106px]"
        :class="isAuthorized ? '!text-sm' : 'font-semibold'"
        :variant="isAuthorized ? 'primary' : 'unstyled'"
        @click="onDownloadClick"
      >
        <CircularProgressBar
          v-if="isDownloading || downloadProgress"
          class="z-50"
          color="white"
          :progress="downloadProgress"
          :is-loading="isDownloading"
        />
        <p v-if="!isDownloading">
          <ClientOnly>
            {{ unAuthLabel }}
          </ClientOnly>
        </p>
      </BaseButton>

      <div v-if="showViewNow" :class="'ml-2 border-l-1 border-white pl-2'">
        <BaseButton
          class="h-[42px] bg-white !text-sm"
          variant="tertiary"
          @click="handleOpenPDFViewer"
        >
          View now
        </BaseButton>
      </div>
    </div>

    <div
      v-if="!isDownloading"
      class="absolute flex h-full w-full cursor-pointer flex-col items-center justify-center md:hidden"
      :class="showOverlayOptions ? 'hidden' : 'flex'"
      @click="openOverlayOptions"
    >
      <div class="rounded-full bg-black/70 p-2">
        <IconDownload class="h-3 w-3 fill-white" />
      </div>
    </div>
  </div>
</template>
